import { ClientError } from 'graphql-request';
import { useCallback } from 'react';
import { ContactDetailsType, VerificationType } from '@brighte/validation';
import { MutateOptions } from '@tanstack/react-query';
import {
  SendEmailVerificationMutation,
  SendSmsVerificationMutation,
  useSendEmailVerificationMutation,
  useSendSmsVerificationMutation,
  useVerifyUpdatedUserEmailMutation,
  useVerifyUpdatedUserMobileMutation,
} from '../graphql';

type VerifyUpdatedUserEmailType = Pick<ContactDetailsType, 'otp' | 'verificationType'> & {
  email: string | undefined;
  mobile: string | undefined;
};
type SendEmailVerificationMutationType = Pick<ContactDetailsType, 'verificationType'> & {
  email: string | undefined;
  mobile: string | undefined;
};

export const useUpdateContactDetails = () => {
  const {
    data: emailVerificationResponse,
    mutate: sendEmailVerificationMutation,
    error: errorSendEmailVerification,
    isLoading: isLoadingSendEmailVerification,
  } = useSendEmailVerificationMutation<ClientError>();
  const {
    data: smsVerificationResponse,
    mutate: sendSmsVerificationMutation,
    error: errorSendSmsVerification,
    isLoading: isLoadingSendSmsVerification,
  } = useSendSmsVerificationMutation<ClientError>();

  const {
    mutate: verifyUpdatedUserEmailMutation,
    isLoading: isLoadingVerifyUpdatedUserEmail,
    error: errorVerifyUpdatedUserSmsMutation,
    data: verifyUpdatedUserEmailResponse,
  } = useVerifyUpdatedUserEmailMutation<ClientError>();

  const {
    mutate: verifyUpdatedUserSmsMutation,
    isLoading: isLoadingVerifyUpdatedUserMobile,
    error: errorUseVerifyUpdatedUserMobile,
    data: verifyUpdatedUserSmsResponse,
  } = useVerifyUpdatedUserMobileMutation<ClientError>();

  const sendUpdateContactDetailsVerification = useCallback(
    (
      { email, mobile, verificationType }: SendEmailVerificationMutationType,
      mutationOptions?: MutateOptions<
        SendEmailVerificationMutation | SendSmsVerificationMutation,
        ClientError,
        unknown
      >,
    ) => {
      switch (true) {
        case email && verificationType === VerificationType.Email:
          sendEmailVerificationMutation(
            {
              email,
            },
            mutationOptions,
          );
          break;
        case mobile && verificationType === VerificationType.Sms:
          sendSmsVerificationMutation(
            {
              mobile: mobile,
            },
            mutationOptions,
          );
          break;
        default:
          break;
      }
    },
    [],
  );

  const verifyUpdateContactDetails = async ({ email, mobile, otp, verificationType }: VerifyUpdatedUserEmailType) => {
    switch (true) {
      case otp && email && verificationType === VerificationType.Email:
        verifyUpdatedUserEmailMutation({
          email: email,
          authCode: otp,
        });

        break;
      case otp && mobile && verificationType === VerificationType.Sms:
        verifyUpdatedUserSmsMutation({
          mobile: mobile,
          authCode: otp,
        });
        break;
      default:
        break;
    }
  };

  return {
    sendUpdateContactDetailsVerification,
    errorField: errorSendEmailVerification ? 'newEmail' : 'newMobile',
    errorSendUpdateContactDetailsVerification: errorSendEmailVerification || errorSendSmsVerification,
    sendUpdateContactDetailsVerificationResponse: emailVerificationResponse || smsVerificationResponse,

    isLoading:
      isLoadingVerifyUpdatedUserMobile ||
      isLoadingVerifyUpdatedUserEmail ||
      isLoadingSendEmailVerification ||
      isLoadingSendSmsVerification,
    isRequestLoading: isLoadingSendEmailVerification || isLoadingSendSmsVerification,
    verifyUpdateContactDetails,
    verifyUpdateContactDetailsResponse: verifyUpdatedUserEmailResponse || verifyUpdatedUserSmsResponse,
    errorVerifyUpdateContactDetails: errorUseVerifyUpdatedUserMobile || errorVerifyUpdatedUserSmsMutation,
  };
};
